import { CategoryQueryParam, DAILY_NEWS_CATEGORY_ID } from 'app/constants/Categories';
import { CourseQueryParam } from 'app/constants/Courses';
import {
  ICategoryMaterialTuple,
  ICourseMaterialTuple,
  ILessonMaterialTuple,
  MaterialTuple,
} from 'app/constants/Materials';
import {
  CategoriesRoute,
  CategoryRoute,
  DailyNewsArticleRoute,
  DailyNewsRoute,
  ViewCurrentPublishedLessonRoute,
  ViewPublishedLessonRoute,
} from 'app/constants/Routes';
import { Category, Course, Lesson, LessonHeader, Text } from 'app/constants/schemas';
import { getEntityOfType } from 'app/models/entities/selectors';
import { LocationDescriptorObject } from 'app/services/history';
import { AppState } from 'app/store';
import { EntityStringId } from 'app/types/entity';
import { entityAnchor } from 'app/utils/routes';
import { stringify } from 'query-string';
import { EntityStringIdRouteCodec } from 'app/constants/Routes/codecs';

enum MaterialRouteType {
  DailyNews,
  Materials,
}

interface IMaterialRouteMeta {
  categoryRoute: typeof DailyNewsRoute | typeof CategoryRoute;
  embedBreadcrumbs: boolean;
  embedCategorySlug: boolean;
  embedCourseSlug: boolean;
  masterLessonRoute: typeof DailyNewsArticleRoute | typeof ViewCurrentPublishedLessonRoute;
  specificLessonRoute: typeof ViewPublishedLessonRoute;
}

// Settings for helping construct lesson routes.
const MaterialRouteMeta: {
  readonly [I in MaterialRouteType]: IMaterialRouteMeta
} = {
  [MaterialRouteType.DailyNews]: {
    categoryRoute: DailyNewsRoute,
    embedBreadcrumbs: false,
    embedCategorySlug: false,
    embedCourseSlug: false,
    masterLessonRoute: DailyNewsArticleRoute,
    specificLessonRoute: ViewPublishedLessonRoute,
  },
  [MaterialRouteType.Materials]: {
    categoryRoute: CategoryRoute,
    embedBreadcrumbs: true,
    embedCategorySlug: true,
    embedCourseSlug: true,
    masterLessonRoute: ViewCurrentPublishedLessonRoute,
    specificLessonRoute: ViewPublishedLessonRoute,
  },
};

/**
 * Gets the appropriate route for a lesson or category.
 */
const getMaterialRouteMeta = (categoryId: EntityStringId | null): IMaterialRouteMeta => {
  return categoryId === DAILY_NEWS_CATEGORY_ID ?
    MaterialRouteMeta[MaterialRouteType.DailyNews] :
    MaterialRouteMeta[MaterialRouteType.Materials];
};

/**
 * Workaround for the fact that there are LessonHeader and Lesson types. For purposes of generating
 * a link, either are fine to use.
 */
const getLesson = (state: AppState, lessonId: EntityStringId): Lesson | LessonHeader | undefined => {
  return getEntityOfType(state, Lesson, lessonId) || getEntityOfType(state, LessonHeader, lessonId);
};

const getLessonRouteHelper = (
  state: AppState,
  lessonMaterial: ILessonMaterialTuple,
  preferMaster: boolean,
) => {
  const { categoryId, courseId, lessonId } = lessonMaterial;

  const category: Category | undefined = getEntityOfType(state, Category, categoryId);
  const course: Course | undefined = getEntityOfType(state, Course, courseId);
  const lesson: Lesson | LessonHeader | undefined = getLesson(state, lessonId);

  if (!lesson) {
    throw new Error('Unable to find lesson.');
  }

  const materialRouteMeta = getMaterialRouteMeta(categoryId);

  // Generate the pathname.
  const slugParts: string[] = [];
  const addSlugPart = (textId: EntityStringId) => {
    const text: Text | undefined = getEntityOfType(state, Text, textId);

    if (text) {
      slugParts.push(text.text);
    }
  };

  if (materialRouteMeta.embedCategorySlug && category) {
    addSlugPart(category.nameText);
  }

  if (materialRouteMeta.embedCourseSlug && course) {
    addSlugPart(course.nameText);
  }

  addSlugPart(lesson.titleText);

  const id = preferMaster && lesson.masterId ? lesson.masterId : lessonId;
  const slug = slugParts.join(' ');

  const pathname = preferMaster ?
    materialRouteMeta.masterLessonRoute.getPathname({ id, slug }) :
    materialRouteMeta.specificLessonRoute.getPathname({ id, slug });

  // Handle generating the query.
  let search = '';

  if (materialRouteMeta.embedBreadcrumbs && categoryId && courseId) {
    search = `?${stringify({
      [CategoryQueryParam.CategoryId]: EntityStringIdRouteCodec.encode(categoryId),
      [CourseQueryParam.CourseId]: EntityStringIdRouteCodec.encode(courseId),
    })}`;
  }

  return { hash: '', pathname, search };
};

type GetLessonRouteParams = (state: AppState, material: ILessonMaterialTuple) => LocationDescriptorObject;

/**
 * Used to go to a specific version of a lesson.
 */
export const getSpecificLessonRoute: GetLessonRouteParams = (state, lessonMaterial) => {
  return getLessonRouteHelper(state, lessonMaterial, false);
};

/**
 * Used to go to the latest version of a lesson.
 */
export const getMasterLessonRoute: GetLessonRouteParams = (state, lessonMaterial) => {
  return getLessonRouteHelper(state, lessonMaterial, true);
};

export const getCategoryRoute = (
  state: AppState,
  material: ICategoryMaterialTuple | ICourseMaterialTuple,
): LocationDescriptorObject => {
  const { categoryId } = material;
  const { categoryRoute } = getMaterialRouteMeta(categoryId);
  let hash = '';

  let pathname: string;

  if (categoryRoute === DailyNewsRoute) {
    pathname = categoryRoute.getPathname();
  } else {
    const category: Category | undefined = getEntityOfType(state, Category, categoryId);
    const text: Text | undefined = getEntityOfType(state, Text, category && category.nameText);
    const slug = text ? text.text : '';

    pathname = categoryRoute.getPathname({ categoryId, slug });

    if ('courseId' in material && material.courseId) {
      hash = entityAnchor(Course, material.courseId, true);
    }
  }

  return { hash, pathname, search: '' };
};

/**
 * Used to go to a particular material in a booking.
 */
export const getMaterialRoute = (state: AppState, material: MaterialTuple): LocationDescriptorObject => {
  if ('lessonId' in material) {
    return getMasterLessonRoute(state, material);
  } else if ('categoryId' in material) {
    return getCategoryRoute(state, material);
  } else {
    return {
      hash: '',
      pathname: CategoriesRoute.getPathname({ languageId: material.language.id }),
      search: '',
    };
  }
};
